<script setup>
import { ref, onBeforeMount, onBeforeUnmount, onMounted } from "vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import api from "@/services/api";

import ArgonInput from "@/components/ArgonInput.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import PasswordInput from "@/components/PasswordInput.vue";
import PasswordValidator from "@/components/PasswordValidator.vue";
import ErrorAlert from "@/components/ErrorAlert.vue";

const body = document.getElementsByTagName("body")[0];
const store = useStore();
const router = useRouter();
const route = useRoute();

const token = ref('');
const email = ref('');
const password = ref('');
const passwordConfirmation = ref('');
const loading = ref(false);
const error = ref('');
const passwordValidatorRef = ref(null);

const toggleDefaultLayout = () => store.commit("toggleDefaultLayout");

onBeforeMount(() => {
  store.state.hideConfigButton = true;
  toggleDefaultLayout();
  body.classList.remove("bg-gray-100");
});

onBeforeUnmount(() => {
  store.state.hideConfigButton = false;
  toggleDefaultLayout();
  body.classList.add("bg-gray-100");
});

onMounted(() => {
  // Obtener token y email de los query parameters de la URL
  token.value = route.query.token || '';
  email.value = route.query.email || '';
  
  console.log('Route params:', route.params);
  console.log('Route query:', route.query);
  console.log('Token:', token.value);
  console.log('Email:', email.value);
  
  if (!token.value) {
    error.value = 'Token de restablecimiento no válido. Por favor, solicita un nuevo enlace.';
  }
  if (!email.value) {
    error.value = 'Email no proporcionado. Por favor, solicita un nuevo enlace.';
  }
});

const validateForm = () => {
  if (!password.value) {
    return 'Por favor ingresa una nueva contraseña';
  }

  // Validación básica de contraseña si el validador no está disponible
  if (password.value.length < 8) {
    return 'La contraseña debe tener al menos 8 caracteres';
  }

  // Usar el validador de contraseña si está disponible
  if (passwordValidatorRef.value && !passwordValidatorRef.value.isValid) {
    return passwordValidatorRef.value.getValidationMessage();
  }
  
  if (password.value !== passwordConfirmation.value) {
    return 'Las contraseñas no coinciden';
  }
  
  return null;
};

const handleResetPassword = async () => {
  console.log('handleResetPassword called');
  console.log('Form values:', {
    token: token.value,
    email: email.value,
    hasPassword: !!password.value,
    hasPasswordConfirmation: !!passwordConfirmation.value
  });

  const validationError = validateForm();
  if (validationError) {
    console.log('Validation error:', validationError);
    error.value = validationError;
    return;
  }

  error.value = '';
  loading.value = true;
  console.log('Starting password reset request...');

  try {
    console.log('Sending reset password request with:', {
      token: token.value,
      email: email.value,
      password: password.value ? '[REDACTED]' : undefined,
      password_confirmation: passwordConfirmation.value ? '[REDACTED]' : undefined
    });

    const response = await api.post('/auth/reset-password', {
      token: token.value,
      email: email.value,
      password: password.value,
      password_confirmation: passwordConfirmation.value
    });

    console.log('Reset password response:', response.data);

    if (response.data.status === 'success') {
      // Mostrar mensaje de éxito
      store.commit('setNotification', {
        type: 'success',
        message: response.data.message || 'Contraseña restablecida exitosamente.'
      });
      
      // Intentar iniciar sesión automáticamente con la nueva contraseña
      try {
        const loginResponse = await store.dispatch('auth/login', {
          email: email.value,
          password: password.value,
        });
        
        if (loginResponse) {
          store.commit('setUser', loginResponse.user);
          // Redirigir al dashboard principal
          router.push('/pacientes');
        }
      } catch (loginError) {
        // Si el login automático falla, redirigir al login manual
        console.warn('Auto login failed, redirecting to login page');
        setTimeout(() => {
          router.push('/login');
        }, 2000);
      }
    }
  } catch (err) {
    console.error('Reset password error:', {
      status: err.response?.status,
      statusText: err.response?.statusText,
      data: err.response?.data,
      message: err.message,
      config: {
        url: err.config?.url,
        method: err.config?.method,
        headers: err.config?.headers
      }
    });

    // Manejar diferentes tipos de errores según la respuesta del backend
    if (err.response?.status === 404) {
      error.value = 'Token de restablecimiento no encontrado o ya utilizado.';
    } else if (err.response?.status === 422) {
      // Errores de validación
      if (err.response?.data?.errors) {
        const errors = err.response.data.errors;
        const errorMessages = Object.values(errors).flat();
        error.value = errorMessages.join('. ');
      } else {
        error.value = 'Los datos proporcionados no son válidos. Verifica que las contraseñas coincidan.';
      }
    } else if (err.response?.status === 400) {
      error.value = 'El token de restablecimiento ha expirado o no es válido. Solicita un nuevo enlace.';
    } else if (err.response?.status === 500 && err.response?.data?.error?.includes('User unknown')) {
      error.value = 'El email asociado a este token no está registrado en nuestro sistema.';
    } else if (err.response?.data?.message) {
      error.value = err.response.data.message;
    } else if (err.code === 'NETWORK_ERROR' || !err.response) {
      error.value = 'Error de conexión. Verifica tu conexión a internet.';
    } else {
      error.value = `Error al restablecer la contraseña (${err.response?.status || 'desconocido'}). Inténtalo de nuevo.`;
    }
  } finally {
    loading.value = false;
  }
};

const goToLogin = () => {
  router.push('/login');
};

const requestNewLink = () => {
  router.push('/forgot-password');
};
</script>

<template>
  <div class="container top-0 position-sticky z-index-sticky">
    <div class="row">
      <div class="col-12">
        <!-- Navbar placeholder -->
      </div>
    </div>
  </div>
  <main class="main-content mt-0">
    <section>
      <div class="page-header min-vh-100">
        <div class="container">
          <div class="row">
            <div class="col-xl-4 col-lg-5 col-md-7 d-flex flex-column mx-lg-0 mx-auto">
              <div class="card card-plain">
                <div class="card-header pb-0 text-left">
                  <h4 class="font-weight-bolder">Restablecer contraseña</h4>
                  <p class="mb-0">Ingresa tu nueva contraseña</p>
                </div>
                <div class="card-body pb-3">
                  <!-- Formulario de restablecimiento -->
                  <form v-if="token && email && !error.includes('Token o email inválido')" @submit.prevent="handleResetPassword">
                    <div class="mb-3">
                      <label>Email</label>
                      <argon-input 
                        id="email" 
                        v-model="email" 
                        type="email" 
                        :disabled="true"
                        aria-label="Email" />
                    </div>
                    
                    <div class="mb-3">
                      <label>Nueva contraseña</label>
                      <password-input 
                        id="password" 
                        v-model="password" 
                        placeholder="Ingresa tu nueva contraseña" 
                        aria-label="Nueva contraseña"
                        :disabled="loading" 
                      />
                      
                      <!-- Validador de contraseña -->
                      <password-validator 
                        ref="passwordValidatorRef"
                        :password="password" 
                      />
                    </div>
                    
                    <div class="mb-3">
                      <label>Confirmar nueva contraseña</label>
                      <password-input 
                        id="passwordConfirmation" 
                        v-model="passwordConfirmation" 
                        placeholder="Confirma tu nueva contraseña" 
                        aria-label="Confirmar contraseña"
                        :disabled="loading" 
                      />
                    </div>
                    
                    <div class="text-center">
                      <argon-button 
                        type="submit"
                        color="success" 
                        variant="gradient" 
                        full-width 
                        class="mt-4 mb-0"
                        :disabled="loading">
                        {{ loading ? 'Restableciendo...' : 'Restablecer contraseña' }}
                      </argon-button>
                    </div>
                  </form>

                  <!-- Error de token inválido -->
                  <div v-if="error.includes('Token o email inválido')" class="text-center">
                    <div class="alert alert-danger">
                      <i class="ni ni-support-16"></i>
                      {{ error }}
                    </div>
                    <argon-button 
                      color="primary" 
                      variant="gradient" 
                      full-width 
                      class="mt-3 mb-2"
                      @click="requestNewLink">
                      Solicitar nuevo enlace
                    </argon-button>
                    <argon-button 
                      color="secondary" 
                      variant="outline" 
                      full-width 
                      @click="goToLogin">
                      Volver al login
                    </argon-button>
                  </div>

                  <!-- Otros errores -->
                  <ErrorAlert 
                    v-if="error && !error.includes('Token o email inválido')" 
                    :error="error" 
                    type="danger" 
                  />

                  <!-- Indicadores de requisitos de contraseña -->
                  <div v-if="password" class="mt-3">
                    <small class="text-muted">Requisitos de la contraseña:</small>
                    <ul class="list-unstyled mt-2">
                      <li class="text-sm" :class="password.length >= 8 ? 'text-success' : 'text-muted'">
                        <i :class="password.length >= 8 ? 'ni ni-check-bold text-success' : 'ni ni-fat-remove text-muted'"></i>
                        Al menos 8 caracteres
                      </li>
                      <li class="text-sm" :class="password === passwordConfirmation && password ? 'text-success' : 'text-muted'">
                        <i :class="password === passwordConfirmation && password ? 'ni ni-check-bold text-success' : 'ni ni-fat-remove text-muted'"></i>
                        Las contraseñas coinciden
                      </li>
                    </ul>
                  </div>
                </div>
                <div class="px-1 pt-0 text-center card-footer px-lg-2">
                  <p class="mx-auto mb-4 text-sm">
                    ¿Recordaste tu contraseña?
                    <router-link
:to="{ name: 'Login' }"
                      class="text-success text-gradient font-weight-bold">Volver al login</router-link>
                  </p>
                </div>
              </div>
            </div>
            <div
              class="col-6 d-lg-flex d-none h-100 my-auto pe-0 position-absolute top-0 end-0 text-center justify-content-center flex-column">
              <div
                class="position-relative bg-gradient-info h-100 m-3 px-7 border-radius-lg d-flex flex-column justify-content-center overflow-hidden">
                <span class="mask bg-info opacity-4"></span>
                <h4 class="mt-5 text-white font-weight-bolder position-relative">
                  Nueva contraseña
                </h4>
                <p class="text-white position-relative">
                  Establece una contraseña segura para proteger tu cuenta
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<style scoped>
.alert {
  padding: 1rem;
  border-radius: 0.375rem;
  margin: 0;
}

.alert-success {
  background-color: #d4edda;
  border-color: #c3e6cb;
  color: #155724;
}

.alert-danger {
  background-color: #f8d7da;
  border-color: #f5c6cb;
  color: #721c24;
}

.list-unstyled {
  list-style: none;
  padding-left: 0;
}

.list-unstyled li {
  padding: 0.25rem 0;
}
</style>